import type { AttributionLike } from '../contracts/dto.js';
import {
  CUSTOMER_SERVICE_SLUGS,
  customerServiceDefinition,
  isCustomerServiceSlug,
  type CustomerServiceSlug,
} from '../contracts/customer-services.js';

/**
 * Runtime configuration for the shared customer client.
 *
 * Everything a deployment needs to differ on — the API origin, how this site
 * authenticates, where the relying-party bridge lives — is a value here. Nothing
 * is baked into the source, so the same built artifact runs on tamoomeh.ir, on
 * porabzar.ir and against a local Core without a rebuild.
 */

/**
 * How the hosting page obtains a customer session.
 *
 * - `same-site`: the page shares a registrable domain with the API origin, so
 *   Core's httpOnly customer cookie is sent directly. This is Tamoomeh.
 * - `sso`: the page is on a different registrable domain, so the browser cookie
 *   cannot be used. The client talks to same-origin relying-party endpoints that
 *   hold the Tamoomeh Core session server-side. This is Porabzar.
 */
export type CustomerAuthMode = 'same-site' | 'sso';

/** Same-origin endpoints a relying site must expose in `sso` mode. */
export interface CustomerBridgeEndpoints {
  /** `GET` — is there a local session? Returns the same shape as Core's session probe. */
  sessionUrl: string;
  /** `GET` — starts the handoff; responds with a redirect to Core's authorize endpoint. */
  loginUrl: string;
  /** `POST` — clears the local session only. */
  logoutUrl: string;
  /** `POST` — creates a request through the relying party's authenticated proxy. */
  requestUrl: string;
  /** `GET` — lists the customer's requests. */
  listUrl?: string;
  /**
   * `POST` (start/resume payment) and `GET` (payment state) — the relying
   * party's authenticated proxy for the customer payment endpoints. Optional:
   * without it the widget simply never offers a Pay button on that site.
   */
  paymentUrl?: string;
  /**
   * `POST` — the relying party's authenticated proxy for the manual receipt
   * upload. Optional: without it the widget never offers the «رسید پرداخت»
   * fallback on that site, because the browser cannot reach Core directly.
   */
  receiptUrl?: string;
}

export interface CustomerClientConfig {
  /**
   * Absolute origin of Tamoomeh Core's API, without a trailing slash.
   *
   * Required for `same-site`, where the widget calls Core directly. **Not used**
   * for `sso`: every call goes to the relying party's own bridge, so requiring an
   * API origin there would force a hardcoded production URL into a static page
   * for no functional reason. Leaving it out is the correct configuration on a
   * relying site.
   */
  apiBaseUrl?: string;
  authMode: CustomerAuthMode;
  /** Required when `authMode` is `sso`. */
  bridge?: CustomerBridgeEndpoints;
  /**
   * Sections this deployment exposes. A widget naming a section that is not in
   * this list renders an error instead of a form, so a typo in an embed cannot
   * silently create a request against the wrong service.
   */
  services?: readonly CustomerServiceSlug[];
  /** Section used when a widget does not name one. */
  defaultService?: CustomerServiceSlug;
  /** Per-request timeout in milliseconds. */
  timeoutMs?: number;
  /** Extra attempts for idempotent reads that fail at the network layer. */
  retries?: number;
  /** Attribution attached to created requests (source page, placement, campaign). */
  attribution?: AttributionLike;
  /** Called for every observable client event; the analytics seam. */
  onEvent?: (event: CustomerClientEvent) => void;
  /** Injected for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Emit diagnostics to the console. Off by default. */
  debug?: boolean;
}

export type CustomerClientEventName =
  | 'config.resolved'
  | 'session.probed'
  | 'otp.requested'
  | 'otp.verified'
  | 'auth.required'
  | 'auth.completed'
  | 'auth.failed'
  | 'logout'
  | 'request.submitted'
  | 'request.failed'
  | 'payment.initiated'
  | 'payment.failed'
  | 'receipt.submitted'
  | 'receipt.failed'
  | 'flow.state';

export interface CustomerClientEvent {
  name: CustomerClientEventName;
  service?: string;
  detail?: Record<string, unknown>;
}

/** The fully-resolved config the client works with. */
export interface ResolvedCustomerClientConfig extends CustomerClientConfig {
  /** Empty string in `sso` mode when no API origin was configured. */
  apiBaseUrl: string;
  authMode: CustomerAuthMode;
  services: readonly CustomerServiceSlug[];
  defaultService: CustomerServiceSlug;
  timeoutMs: number;
  retries: number;
}

const DEFAULT_TIMEOUT_MS = 15_000;
const DEFAULT_RETRIES = 1;

/**
 * The two spellings of the local machine.
 *
 * They are different hosts as far as cookies are concerned, so a page on one
 * cannot send a `SameSite=Lax` cookie set by the other. Both are legitimate
 * local development origins, so the API origin is aligned to whichever alias
 * the page itself used.
 */
const LOOPBACK_HOSTS = new Set(['127.0.0.1', 'localhost']);

/**
 * Keep a loopback API origin on the same host alias as the page.
 *
 * Core's customer cookie is host-scoped and `SameSite=Lax`: a widget served
 * from `http://localhost:5173` cannot use a session cookie set by
 * `http://127.0.0.1:4000`, and vice versa. Both aliases are supported local
 * origins, so when the page and the configured API origin are loopback aliases
 * of each other, the host is rewritten to match the page. Production origins
 * are never loopback, so this is a no-op there.
 */
export function alignLoopbackOrigin(
  apiBaseUrl: string,
  pageHostname: string | null | undefined,
): string {
  if (!apiBaseUrl || !pageHostname) return apiBaseUrl;
  try {
    const url = new URL(apiBaseUrl);
    if (!LOOPBACK_HOSTS.has(url.hostname) || !LOOPBACK_HOSTS.has(pageHostname)) return apiBaseUrl;
    url.hostname = pageHostname;
    return url.toString().replace(/\/+$/, '');
  } catch {
    return apiBaseUrl;
  }
}

/** Data attributes a `<script>` tag may carry to configure the bundle. */
export const SCRIPT_CONFIG_ATTRIBUTES = {
  apiBaseUrl: 'data-api-base',
  authMode: 'data-auth-mode',
  bridgeSession: 'data-bridge-session',
  bridgeLogin: 'data-bridge-login',
  bridgeLogout: 'data-bridge-logout',
  bridgeRequest: 'data-bridge-request',
  bridgeList: 'data-bridge-list',
  bridgePayment: 'data-bridge-payment',
  bridgeReceipt: 'data-bridge-receipt',
  services: 'data-services',
  defaultService: 'data-default-service',
  debug: 'data-debug',
} as const;

/** Global a host page may set instead of (or before) the script tag. */
export const CONFIG_GLOBAL = 'TAMOOMEH_CUSTOMER_CONFIG';

export function normalizeConfig(config: CustomerClientConfig): ResolvedCustomerClientConfig {
  const apiBaseUrl = (config.apiBaseUrl ?? '').replace(/\/+$/, '');
  if (config.authMode !== 'sso' && !apiBaseUrl) {
    throw new Error(
      'Tamoomeh Core customer client: apiBaseUrl is required when authMode is "same-site".',
    );
  }
  if (config.authMode === 'sso' && !config.bridge?.sessionUrl) {
    throw new Error(
      'Tamoomeh Core customer client: authMode "sso" requires bridge.sessionUrl so the client can find its local session.',
    );
  }

  const services = (config.services ?? CUSTOMER_SERVICE_SLUGS).filter(isCustomerServiceSlug);
  if (services.length === 0) {
    throw new Error('Tamoomeh Core customer client: at least one customer service must be enabled.');
  }

  const defaultService =
    config.defaultService && services.includes(config.defaultService)
      ? config.defaultService
      : services[0]!;

  return {
    ...config,
    apiBaseUrl,
    authMode: config.authMode,
    services,
    defaultService,
    timeoutMs: config.timeoutMs && config.timeoutMs > 0 ? config.timeoutMs : DEFAULT_TIMEOUT_MS,
    retries: config.retries !== undefined && config.retries >= 0 ? config.retries : DEFAULT_RETRIES,
  };
}

function splitList(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Build a config from a `<script>` element's data attributes. */
export function configFromScriptElement(
  script: { getAttribute(name: string): string | null } | null,
  base: Partial<CustomerClientConfig> = {},
): CustomerClientConfig | null {
  return configFromAttributes((name) => script?.getAttribute(name) ?? null, base);
}

/**
 * Build a config from a `<script>` element's data attributes.
 *
 * Kept separate from the DOM scan below so it is directly unit-testable: it
 * takes a plain attribute getter, not an element.
 */
export function configFromAttributes(
  get: (name: string) => string | null,
  base: Partial<CustomerClientConfig> = {},
): CustomerClientConfig | null {
  const apiBaseUrl = get(SCRIPT_CONFIG_ATTRIBUTES.apiBaseUrl) ?? base.apiBaseUrl;
  const authMode = (get(SCRIPT_CONFIG_ATTRIBUTES.authMode) ?? base.authMode ?? 'same-site') as CustomerAuthMode;
  // A relying site configures only its own bridge endpoints; an API origin is
  // meaningful only where the widget talks to Core directly.
  if (!apiBaseUrl && authMode !== 'sso') return null;
  const bridgeSession = get(SCRIPT_CONFIG_ATTRIBUTES.bridgeSession);
  const bridgeLogin = get(SCRIPT_CONFIG_ATTRIBUTES.bridgeLogin);
  const bridgeLogout = get(SCRIPT_CONFIG_ATTRIBUTES.bridgeLogout);
  const bridgeRequest = get(SCRIPT_CONFIG_ATTRIBUTES.bridgeRequest);
  const bridgeList = get(SCRIPT_CONFIG_ATTRIBUTES.bridgeList);
  const bridgePayment = get(SCRIPT_CONFIG_ATTRIBUTES.bridgePayment);
  const bridgeReceipt = get(SCRIPT_CONFIG_ATTRIBUTES.bridgeReceipt);

  const bridge: CustomerBridgeEndpoints | undefined =
    bridgeSession && bridgeLogin && bridgeLogout && bridgeRequest
      ? {
          sessionUrl: bridgeSession,
          loginUrl: bridgeLogin,
          logoutUrl: bridgeLogout,
          requestUrl: bridgeRequest,
          ...(bridgeList ? { listUrl: bridgeList } : {}),
          ...(bridgePayment ? { paymentUrl: bridgePayment } : {}),
          ...(bridgeReceipt ? { receiptUrl: bridgeReceipt } : {}),
        }
      : base.bridge;

  const services = splitList(get(SCRIPT_CONFIG_ATTRIBUTES.services)).filter(isCustomerServiceSlug);
  const defaultServiceRaw = get(SCRIPT_CONFIG_ATTRIBUTES.defaultService);
  const debug = get(SCRIPT_CONFIG_ATTRIBUTES.debug);

  return {
    ...base,
    ...(apiBaseUrl ? { apiBaseUrl } : {}),
    authMode,
    ...(bridge ? { bridge } : {}),
    ...(services.length > 0 ? { services: services as CustomerServiceSlug[] } : {}),
    ...(defaultServiceRaw && isCustomerServiceSlug(defaultServiceRaw)
      ? { defaultService: defaultServiceRaw }
      : {}),
    ...(debug === 'true' || debug === '1' ? { debug: true } : {}),
  };
}

interface DocumentLike {
  currentScript?: { getAttribute(name: string): string | null } | null;
  querySelector(selector: string): { getAttribute(name: string): string | null } | null;
}

/**
 * Resolve configuration from the host document.
 *
 * Precedence, most specific first:
 *   1. `data-*` attributes on the currently-executing `<script>`,
 *   2. `window.TAMOOMEH_CUSTOMER_CONFIG` (set by an inline bootstrap),
 *   3. `<meta name="tamoomeh:api-base">` plus friends.
 *
 * Returning `null` when nothing usable can be found is intentional: the element
 * then renders a clear "not configured" state instead of guessing a production
 * URL, which is exactly the failure that would otherwise ship silently.
 */
export function resolveConfigFromDocument(
  doc: DocumentLike | null = typeof document === 'undefined' ? null : (document as unknown as DocumentLike),
  globalObject: Record<string, unknown> | null = typeof window === 'undefined'
    ? null
    : (window as unknown as Record<string, unknown>),
): ResolvedCustomerClientConfig | null {
  const fromGlobal =
    globalObject && typeof globalObject[CONFIG_GLOBAL] === 'object' && globalObject[CONFIG_GLOBAL]
      ? (globalObject[CONFIG_GLOBAL] as Partial<CustomerClientConfig>)
      : undefined;

  if (!doc) {
    return fromGlobal?.apiBaseUrl ? normalizeConfig(fromGlobal as CustomerClientConfig) : null;
  }

  const current = doc.currentScript ?? null;
  const meta = (name: string): string | null =>
    doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null;

  const attributeGetter = (name: string): string | null => {
    const fromScript = current?.getAttribute(name) ?? null;
    if (fromScript) return fromScript;
    switch (name) {
      case SCRIPT_CONFIG_ATTRIBUTES.apiBaseUrl:
        return meta('tamoomeh:api-base');
      case SCRIPT_CONFIG_ATTRIBUTES.authMode:
        return meta('tamoomeh:auth-mode');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgeSession:
        return meta('tamoomeh:bridge-session');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgeLogin:
        return meta('tamoomeh:bridge-login');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgeLogout:
        return meta('tamoomeh:bridge-logout');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgeRequest:
        return meta('tamoomeh:bridge-request');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgeList:
        return meta('tamoomeh:bridge-list');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgePayment:
        return meta('tamoomeh:bridge-payment');
      case SCRIPT_CONFIG_ATTRIBUTES.bridgeReceipt:
        return meta('tamoomeh:bridge-receipt');
      case SCRIPT_CONFIG_ATTRIBUTES.services:
        return meta('tamoomeh:services');
      case SCRIPT_CONFIG_ATTRIBUTES.defaultService:
        return meta('tamoomeh:default-service');
      default:
        return null;
    }
  };

  const raw = configFromAttributes(attributeGetter, fromGlobal ?? {});
  if (!raw) return null;
  const resolved = normalizeConfig(raw);
  const location = globalObject?.['location'] as { hostname?: unknown } | undefined;
  const pageHostname = typeof location?.hostname === 'string' ? location.hostname : null;
  return { ...resolved, apiBaseUrl: alignLoopbackOrigin(resolved.apiBaseUrl, pageHostname) };
}

/** True when the slug is enabled in this deployment. */
export function isEnabledService(
  config: ResolvedCustomerClientConfig,
  slug: string | null | undefined,
): slug is CustomerServiceSlug {
  return isCustomerServiceSlug(slug) && config.services.includes(slug);
}

/** Resolve a widget's service attribute, falling back to the deployment default. */
export function resolveWidgetService(
  config: ResolvedCustomerClientConfig,
  requested: string | null | undefined,
): { slug: CustomerServiceSlug | null; problem: string | null } {
  if (!requested) return { slug: config.defaultService, problem: null };
  const definition = customerServiceDefinition(requested);
  if (!definition) {
    return { slug: null, problem: `سرویس «${requested}» شناخته‌شده نیست.` };
  }
  if (!config.services.includes(definition.slug)) {
    return { slug: null, problem: `سرویس «${definition.nameFa}» در این نسخه فعال نیست.` };
  }
  return { slug: definition.slug, problem: null };
}
